import { useState, useEffect } from "react";
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from "../../services/api/api";
import {
    Search, Loader2, User, BookOpen,
    CheckCircle2, X, Eye, EyeOff, NotebookPen
} from "lucide-react";
import { Dialog, Zoom, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import AdicionarLivro from "../../pages/adicionarLivro";
import { FastAverageColor } from "fast-average-color";
import ScrollToTop from "../scrollToTop";

import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

export default function BooksManagement() {
    const api = authApi();
    const queryClient = useQueryClient();
    const { ref, inView } = useInView();

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");

    const [openEdit, setOpenEdit] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);
    const themeMui = useTheme();

    const isMobile = useMediaQuery(themeMui.breakpoints.down('sm'));
    const getData = async ({ pageParam = 1 }) => {
        try {
            let response;

            // 1. Aba de Atrasados/Alugados
            if (filter === 'overdue') {
                response = await api.listRentals(pageParam, search);
                // Aqui não sobrescrevemos 'user' nem 'start/end', pois a API já envia
                return response;
            }

            // 2. Abas de Gestão de Acervo (Ativos/Inativos)
            const statusFilter = filter === 'inactive' ? 1 : 0;
            response = await api.list(pageParam, search, "", "todos", "", "", statusFilter);

            if (response && response.data) {
                return {
                    ...response,
                    data: response.data.map((book: any) => ({
                        id: `catalog-${book.id}`,
                        books: book, // A API de listagem comum traz o objeto do livro direto
                        user: null,
                        start: null,
                        end: null,
                        isBookOnly: true
                    }))
                };
            }
            return { data: [], current_page: 1, last_page: 1 };
        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            return { data: [], current_page: 1, last_page: 1 };
        }
    };

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ['books', search, filter],
        queryFn: getData,
        initialPageParam: 1,
        getNextPageParam: (lastPage: any) => lastPage?.current_page < lastPage?.last_page ? lastPage.current_page + 1 : undefined,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
    }, [inView, hasNextPage, isFetchingNextPage]);

    const handleEditOpen = (book: any) => {
        setSelectedBook(book);
        setOpenEdit(true);
    };

    const checkStatus = (dueDate: string) => {
        if (!dueDate) return 'no_prazo';
        return new Date(dueDate) < new Date() ? 'atrasado' : 'no_prazo';
    };

    return (
        <div className="w-full animate-in fade-in duration-500 font-montserrat">
            <div className="flex flex-col md:flex-row gap-6 mb-8 justify-between items-center">
                <div className="relative group w-full max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar no acervo..."
                        className="placeholder:text-xs w-full pl-12 pr-4 py-2.5 rounded-2xl bg-white dark:bg-white/10 border border-gray-100 dark:border-white/5 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-sm shadow-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>

                <div className="flex xs:w-full md:w-auto bg-white dark:bg-[#001e42] p-1 rounded-2xl border border-gray-100 dark:border-white/20 shadow-inner space-x-1">
                    {[
                        { id: 'all', label: 'Ativos' },
                        { id: 'inactive', label: 'Inativos' },
                        { id: 'overdue', label: 'Atrasados/Alugados' },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setFilter(t.id)}
                            className={`lg:px-6 px-3.5 py-2 xs:w-full md:w-auto rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${filter === t.id ? 'bg-blue3 text-white shadow' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-white/10'}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 lg:gap-6">
                {data?.pages?.map((page: any) => (
                    page?.data?.map((item: any) => (
                        <ManagementCard
                            key={item.id}
                            item={item}
                            checkStatus={checkStatus}
                            onEdit={() => handleEditOpen(item.books)}
                        />
                    ))
                ))}
            </div>

            <div ref={ref} className="h-24 flex justify-center items-center mt-8">
                {isFetchingNextPage && <Loader2 className="animate-spin text-blue-600" size={36} />}
            </div>

            <Dialog
                open={openEdit}
                onClose={() => setOpenEdit(false)}
                TransitionComponent={Zoom}
                maxWidth="md"
                disableScrollLock
                fullScreen={isMobile}
                PaperProps={{
                    style: {
                        borderRadius: isMobile ? 0 : '24px',
                        backgroundImage: 'none',
                        overflow: 'hidden' // Garante que o header não vaze o border-radius
                    },
                }}>
                <div className="bg-gradient-to-r from-blue3 to-blue4 px-8 py-6 flex justify-between items-center shadow-lg relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="bg-white/10 p-3 rounded-2xl backdrop-blur-md border border-white/20 text-white">
                            <NotebookPen size={17} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white tracking-tight leading-none">
                                Editar Exemplar
                            </h2>
                            <p className="text-blue-100 text-[10px] uppercase tracking-[0.2em] font-bold mt-1.5 opacity-70">
                                Gestão de Acervo Digital
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => setOpenEdit(false)}
                        className="p-2 rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-all active:scale-90 absolute top-2 right-2"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="xs:p-4 md:p-8 md:max-h-[80vh] max-w-none overflow-y-auto custom-scrollbar">
                    <AdicionarLivro bookData={selectedBook} onSuccess={() => { setOpenEdit(false); queryClient.invalidateQueries({ queryKey: ['books'] }); }} />
                </div>
            </Dialog>
            <ScrollToTop />
            <ToastContainer />
        </div>
    );
}

function ManagementCard({ item, onEdit, checkStatus }: any) {
    const api = authApi();
    const queryClient = useQueryClient();
    const book = item.books;
    const isInactive = Number(book.status) === 1;
    const isOverdue = item.end ? checkStatus(item.end) === 'atrasado' : false;

    const [openDialog, setOpenDialog] = useState(false);
    const [gradientColor, setGradientColor] = useState('rgba(0,0,0,0.6)');

    useEffect(() => {
        if (book?.img) {
            new FastAverageColor().getColorAsync(book.img)
                .then(c => setGradientColor(`linear-gradient(to bottom, ${c.rgba}, rgba(0,0,0,0.85))`))
                .catch(() => setGradientColor('rgba(0,10,30,0.8)'));
        }
    }, [book?.img]);

    const toggleMutation = useMutation<any, Error, boolean>({
        mutationFn: (newStatus: boolean) => api.updateBook({ ...book, book_id: book.id, status: newStatus ? 1 : 0 }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['books'] });
            toast.success("Status atualizado!");
        }
    });

    const returnMutation = useMutation({
        mutationFn: () => api.returnBook(item.id),
        onSuccess: () => {
            toast.success("Livro devolvido!");
            queryClient.invalidateQueries({ queryKey: ['books'] });
            setOpenDialog(false);
        },
        onError: () => toast.error("Erro na devolução.")
    });

    return (
        <div className={`group relative bg-white dark:bg-[#2d3d5780] rounded-lg overflow-hidden border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full font-montserrat
            ${isInactive ? 'opacity-60 grayscale-[0.5] border-red-200' : 'border-gray-100 dark:border-white/5 hover:shadow-xl'}`}>

            <div className="relative h-48 flex justify-center items-center overflow-hidden bg-slate-100  p-4">
                <img src={book.img} className="max-h-40 object-contain z-10 transition-transform duration-500 group-hover:scale-110 drop-shadow-2xl" alt={book.title} />
                <div className="absolute inset-0" style={{ background: gradientColor }} />
                <div className="absolute top-4 right-2 z-20 flex gap-1">
                    <button
                        className="p-2 rounded-full bg-blue3 text-white shadow-md hover:bg-blue-700 transition-all active:scale-90"
                        onClick={onEdit}
                        title="Editar Livro"
                    >
                        <NotebookPen size={17} />
                    </button>
                    <button
                        className={`p-2 rounded-full shadow-md transition-all active:scale-90 ${isInactive ? 'bg-red-500 text-white' : 'bg-green-700 text-white'}`}
                        onClick={() => toggleMutation.mutate(!isInactive)}
                        title={isInactive ? "Ativar Livro" : "Inativar Livro"}
                    >
                        {isInactive ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                </div>

                {/* Badge de Atraso (se existir usuário) */}
                {item.user && (
                    <div className="absolute top-4 left-2 z-20">
                        <span className={`px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest shadow-lg ${isOverdue ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`}>
                            {isOverdue ? 'Atrasado' : 'Reservado'}
                        </span>
                    </div>
                )}
            </div>

            <div className="p-4 flex flex-col flex-1">
                <h4 className="font-black text-[11px] text-slate-800 dark:text-white uppercase truncate">{book.title}</h4>
                <p className="text-[9px] text-slate-400 font-bold mt-1">{book.autor}</p>

                {item.user && (
                    <div className=" py-3  flex items-center gap-2">
                        <User size={12} className="text-blue3 dark:text-blue5" />
                        <span className="text-[10px] font-bold text-slate-600 truncate dark:text-white/80">{item.user.name}</span>
                    </div>
                )}
                {item.user && (
                    <div>
                        <div className="grid grid-cols-2 gap-2 py-3 border-t border-gray-100 dark:border-white/5 mt-auto">
                            <div className="flex flex-col">
                                <span className="text-[8px] uppercase font-bold text-gray-400 tracking-tighter">Retirada</span>
                                <span className="text-[10px] font-bold dark:text-gray-300">
                                    {new Date(item.start).toLocaleDateString('pt-BR')}
                                </span>
                            </div>
                            <div className="flex flex-col text-right">
                                <span className="text-[8px] uppercase font-bold text-gray-400 tracking-tighter">Prazo</span>
                                <span className={`text-[10px] font-bold ${isOverdue ? 'text-red-500' : 'text-blue-500'}`}>
                                    {new Date(item.end).toLocaleDateString('pt-BR')}
                                </span>
                            </div>
                        </div>

                        <button
                            onClick={() => setOpenDialog(true)}
                            className={`mt-3 w-full py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border ${isOverdue ? 'bg-red-50 text-red-600 border-red-100 hover:bg-red-600 hover:text-white' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-600 hover:text-white dark:bg-white/5 dark:text-gray-300 dark:border-white/10 dark:hover:bg-blue-600 dark:hover:text-white'
                                }`}
                        >
                            Devolver
                        </button>
                    </div>
                )}


            </div>
            <ReturnConfirmDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={() => returnMutation.mutate()}
                item={item}
                loading={returnMutation.isPending}
            />

        </div>
    );
}

const ReturnConfirmDialog = ({ open, onClose, onConfirm, item, loading }: any) => {
    if (!item) return null;
    return (
        <Dialog open={open} onClose={onClose} TransitionComponent={Zoom} disableScrollLock PaperProps={{
            style: {
                borderRadius: '32px',
                backgroundColor: '#ffffff',
                backgroundImage: 'none',
                maxWidth: '500px',
                width: '100%'
            },
        }}>
            <div className="bg-gradient-to-r from-blue3 to-blue4 px-6 py-6 flex justify-between items-center shadow-lg">
                <div className="flex items-center gap-3">
                    <div className="bg-white/10 p-2.5 rounded-xl backdrop-blur-md border border-white/20 text-white">
                        <CheckCircle2 size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white tracking-tight leading-none">Confirmar Recebimento</h2>
                        <p className="text-blue-100 text-[9px] uppercase tracking-widest font-medium mt-1">Gestão de Acervo</p>
                    </div>
                </div>
                <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
                    <X size={20} />
                </button>
            </div>

            <DialogContent className="pt-8 pb-4">
                <div className="flex flex-col items-center text-center gap-5 font-montserrat">
                    <div className="relative">
                        <img
                            src={item.books?.img}
                            className="w-24 h-32 object-cover rounded-2xl shadow-2xl border-4 border-white transform -rotate-2"
                            alt=""
                        />
                        <div className="absolute -bottom-2 -right-2 bg-emerald-500 p-2 rounded-full border-4 border-white shadow-lg text-white">
                            <BookOpen size={16} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 px-4">
                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest leading-none">Leitor:</p>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight leading-tight">
                            {item.user?.name}
                        </h3>
                        <div className="flex items-center justify-center gap-2 text-blue-600 font-bold text-xs bg-blue-50 py-2 px-4 rounded-full mt-2">
                            <BookOpen size={14} />
                            {item.books?.title}
                        </div>
                    </div>

                    <div className="w-full grid grid-cols-2 gap-4 mt-2 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className="text-left border-r border-slate-200 pr-2">
                            <span className="text-[8px] font-black text-slate-400 uppercase block">Alugado em</span>
                            <span className="text-xs font-bold text-slate-600">{new Date(item.start).toLocaleDateString('pt-BR')}</span>
                        </div>
                        <div className="text-left pl-2">
                            <span className="text-[8px] font-black text-slate-400 uppercase block">Data de Hoje</span>
                            <span className="text-xs font-bold text-emerald-600">{new Date().toLocaleDateString('pt-BR')}</span>
                        </div>
                    </div>
                </div>
            </DialogContent>

            <DialogActions className="p-6 pt-2 flex gap-3 border-t border-gray-100">
                <div className="w-full flex my-2">
                    <button onClick={onClose} className="flex-1 h-12 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-400 hover:bg-slate-100 transition-all">
                        Voltar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-[2] h-12 bg-blue3 hover:bg-blue4 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 uppercase tracking-widest text-[10px] transition-all active:scale-[0.98] flex items-center justify-center"
                    >
                        {loading ? <CircularProgress size={18} color="inherit" /> : "Confirmar Devolução"}
                    </button>
                </div>
            </DialogActions>
        </Dialog>
    );
};